import { useEffect, useState, useMemo } from "react";
import DashboardLayout from "../../../layouts/DashboardLayout";
import api from "../../../api";

// Child component for table
function HistoryTable({ title, data, rowsPerPage, page, onPageChange, searchTerm, fileName }) {
    // filter + paginate inside the child
    const filtered = useMemo(() => {
      if (!searchTerm) return data;
      const lower = searchTerm.toLowerCase();
      return data.filter(
        (d) =>
          (d.transaction_type && d.transaction_type.toLowerCase().includes(lower)) ||
          (d.source_name && d.source_name.toLowerCase().includes(lower)) ||
          (d.destination_name && d.destination_name.toLowerCase().includes(lower))
      );
    }, [data, searchTerm]);
  
    const paged = useMemo(() => {
      const start = (page - 1) * rowsPerPage;
      return filtered.slice(start, start + rowsPerPage);
    }, [filtered, page, rowsPerPage]);
  
    const totalPages = Math.ceil(filtered.length / rowsPerPage) || 1;
  
    const exportCSV = () => {
      if (!filtered || !filtered.length) return;
      const keys = Object.keys(filtered[0]);
      const csv = [
        keys.join(","),
        ...filtered.map((row) =>
          keys.map((k) => JSON.stringify(row[k] ?? "")).join(",")
        ),
      ].join("\n");
  
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.click();
    };
  
    return (
      <div className="bg-white shadow rounded-lg p-4 overflow-x-auto mb-6">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-xl font-semibold">{title}</h2>
          <button
            onClick={exportCSV}
            className="bg-blue-600 text-white px-3 py-1 rounded-lg hover:bg-blue-700"
          >
            Export CSV
          </button>
        </div>
  
        {filtered.length === 0 ? (
          <p className="text-gray-500">No records found.</p>
        ) : (
          <>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="text-gray-600 border-b">
                  <th className="py-2 px-3">ID</th>
                  <th className="py-2 px-3">Transaction Type</th>
                  <th className="py-2 px-3">Total Credit</th>
                  <th className="py-2 px-3">Source</th>
                  <th className="py-2 px-3">Destination</th>
                  <th className="py-2 px-3">Created At</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((h) => (
                  <tr key={h.id} className="border-b hover:bg-gray-50">
                    <td className="py-2 px-3">{h.id}</td>
                    <td className="py-2 px-3">{h.transaction_type || "-"}</td>
                    <td className="py-2 px-3">{h.total_credit}</td>
                    <td className="py-2 px-3">{h.source_name || h.source_id}</td>
                    <td className="py-2 px-3">
                      {h.destination_name || h.destination_id || "-"}
                    </td>
                    <td className="py-2 px-3">
                      {new Date(h.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
  
            <div className="flex items-center gap-2 mt-2">
              <button
                onClick={() => onPageChange(Math.max(page - 1, 1))}
                disabled={page === 1}
                className="px-2 py-1 bg-gray-200 rounded"
              >
                Prev
              </button>
              <span>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => onPageChange(Math.min(page + 1, totalPages))}
                disabled={page === totalPages}
                className="px-2 py-1 bg-gray-200 rounded"
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  export default function CreditHistory() {
    const [personalHistory, setPersonalHistory] = useState([]);
    const [orgHistory, setOrgHistory] = useState([]);
    const [personalTotal, setPersonalTotal] = useState(0);
    const [orgTotal, setOrgTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
  
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState("");
  
    useEffect(() => {
      const loadHistories = async () => {
        setLoading(true);
        try {
          const query = `?page=${page}&per_page=${rowsPerPage}&search=${encodeURIComponent(
            searchTerm
          )}`;
          const res = await api.fetchcredithistory(query);
  
          setPersonalHistory(res.data.personal_credit_history || []);
          setOrgHistory(res.data.org_credit_history || []);
          setPersonalTotal(res.data.personal_total || 0);
          setOrgTotal(res.data.org_total || 0);
        } catch (err) {
          setError("Failed to load credit history");
        } finally {
          setLoading(false);
        }
      };
      loadHistories();
    }, [page, rowsPerPage, searchTerm]);
  
    return (
      <DashboardLayout>
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Credit History</h1>
          <input
            type="text"
            placeholder="Search by transaction / source / destination"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            className="border p-2 rounded w-1/3"
          />
        </div>
  
        {loading ? (
          <p>Loading credit history...</p>
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : (
          <>
            <HistoryTable
              title="Personal Credit History"
              data={personalHistory}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={setPage}
              searchTerm={searchTerm}
              fileName="personal_credit_history.csv"
            />
            {orgHistory.length > 0 && (
              <HistoryTable
                title="Organisation Credit History"
                data={orgHistory}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={setPage}
                searchTerm={searchTerm}
                fileName="organisation_credit_history.csv"
              />
            )}
          </>
        )}
      </DashboardLayout>
    );
  }
  
